import { AddressResultType, AddressSearchResult } from '@app/api/dmc';
import { AddressType } from '@app/core/types/address-type';
import { SearchResult, SearchResultCollection } from '@shared/components/input/search/search-result.interface';
import { map, Observable } from 'rxjs';
import { House, type LucideIconData, MapPin, Route, Search } from 'lucide-angular';

export interface DarSearchResult extends SearchResult {
  type: AddressResultType;
}

const roadIcon: LucideIconData = [
  ['path', { d: 'M7 3 3 21' }],
  ['path', { d: 'M17 3 21 21' }],
  ['path', { d: 'M12 3v3m0 4v4m0 4v3' }],
];

const resultPresentation: Record<AddressResultType, Pick<SearchResult, 'icon' | 'iconLabel'>> = {
  [AddressResultType.Street]: { icon: Route, iconLabel: 'address.streetName' },
  [AddressResultType.StreetPostalCode]: { icon: roadIcon, iconLabel: 'address.streetPostalCode' },
  [AddressResultType.AccessAddress]: { icon: MapPin, iconLabel: 'enum.address.access' },
  [AddressResultType.Address]: { icon: House, iconLabel: 'enum.address.address' },
  [AddressResultType.Unknown]: { icon: Search, iconLabel: 'search.results.title' },
};

const refinementOrder: readonly AddressResultType[] = [
  AddressResultType.Street,
  AddressResultType.StreetPostalCode,
  AddressResultType.AccessAddress,
  AddressResultType.Address,
];

/**
 * Only results matching the requested address type complete selection. Intermediate
 * street suggestions refine the search, while buildings load their addresses by ID.
 */
export function mapDarResults(
  results: AddressSearchResult[] | null | undefined,
  type: AddressType,
  search: (text: string) => Observable<AddressSearchResult[]>,
  getByAccessAddressId: (id: string) => Observable<AddressSearchResult[]>,
): SearchResultCollection[] {
  const finalType = type === 'access' ? AddressResultType.AccessAddress : AddressResultType.Address;
  const finalLevel = refinementOrder.indexOf(finalType);
  const supportedResults = (results ?? []).filter(result => {
    const level = refinementOrder.indexOf(result.type);
    return level >= 0 && level <= finalLevel;
  });

  return [{
    results: supportedResults.map((result): DarSearchResult => {
      const suggestion: DarSearchResult = {
        id: result.id,
        title: result.title,
        type: result.type,
        ...resultPresentation[result.type],
      };

      if (result.type !== finalType) {
        if (result.type === AddressResultType.StreetPostalCode) {
          suggestion.childrenLabel = 'address.chooseAnyAddress';
        } else {
          suggestion.childrenLabel = type === 'access' || result.type === AddressResultType.Street
            ? 'address.chooseAccessAddress'
            : 'address.chooseAddress';
        }
        suggestion.children = () => (result.type === AddressResultType.AccessAddress
          ? getByAccessAddressId(result.id)
          : search(result.title)).pipe(
          map(children => {
            const matches = children.filter(child => {
              if (type === 'address' && result.type === AddressResultType.AccessAddress) {
                // The exact lookup supplies a canonical owner ID. Never accept another building's addresses.
                return child.type === AddressResultType.Address
                  && child.accessAddressId?.toLowerCase() === result.id.toLowerCase();
              }

              return refinementOrder.indexOf(child.type) > refinementOrder.indexOf(result.type);
            });
            return mapDarResults(matches, type, search, getByAccessAddressId);
          }),
        );
      }

      return suggestion;
    }),
  }];
}
